// Sentence-aware renderer for the reader: parses light markdown into blocks,
// splits text into sentences with stable global indices, and renders each
// sentence as a span so audio playback / citation focus can highlight it.
import React, { useEffect } from "react";

function splitSentences(text) {
  const clean = text.replace(/\*\*/g, "").trim();
  const matches = clean.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g);
  return (matches || [clean]).map((s) => s.trim()).filter(Boolean);
}

// Returns { blocks, sentences } where sentences is the global ordered list.
export function parseSpoken(text) {
  const lines = (text || "").split("\n");
  const blocks = [];
  const sentences = [];
  let list = null;
  let listTag = null;

  const addSentences = (str) => {
    const out = [];
    for (const s of splitSentences(str)) {
      out.push({ i: sentences.length, text: s });
      sentences.push(s);
    }
    return out;
  };
  const flush = () => {
    if (list) { blocks.push({ tag: listTag, items: list }); list = null; listTag = null; }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (line.startsWith("### ")) { flush(); blocks.push({ tag: "h3", sents: addSentences(line.slice(4)) }); return; }
    if (line.startsWith("## ")) { flush(); blocks.push({ tag: "h2", sents: addSentences(line.slice(3)) }); return; }
    if (line.startsWith("# ")) { flush(); blocks.push({ tag: "h2", sents: addSentences(line.slice(2)) }); return; }
    if (line.startsWith("> ")) { flush(); blocks.push({ tag: "blockquote", sents: addSentences(line.slice(2)) }); return; }
    const ol = line.match(/^\d+\.\s+(.*)/);
    const ul = line.match(/^[-*]\s+(.*)/);
    if (ol) { if (listTag !== "ol") flush(); listTag = "ol"; list = list || []; list.push(addSentences(ol[1])); return; }
    if (ul) { if (listTag !== "ul") flush(); listTag = "ul"; list = list || []; list.push(addSentences(ul[1])); return; }
    flush();
    blocks.push({ tag: "p", sents: addSentences(line) });
  });
  flush();
  return { blocks, sentences };
}

function Sent({ s, active, focus }) {
  const cls = `sent${s.i === active ? " sent-active" : ""}${s.i === focus ? " sent-focus" : ""}`;
  return <span className={cls} data-sentence={s.i}>{s.text}{" "}</span>;
}

export default function SpokenText({ blocks, active = -1, focus = -1 }) {
  useEffect(() => {
    const idx = active >= 0 ? active : focus;
    if (idx >= 0) {
      const el = document.querySelector(`[data-sentence="${idx}"]`);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [active, focus]);

  return (
    <div className="prose-indic">
      {blocks.map((b, bi) => {
        if (b.tag === "ul" || b.tag === "ol") {
          const Tag = b.tag;
          return (
            <Tag key={bi} className={b.tag === "ol" ? "list-decimal" : "list-disc"}>
              {b.items.map((sents, li) => (
                <li key={li}>{sents.map((s) => <Sent key={s.i} s={s} active={active} focus={focus} />)}</li>
              ))}
            </Tag>
          );
        }
        const Tag = b.tag;
        return <Tag key={bi}>{b.sents.map((s) => <Sent key={s.i} s={s} active={active} focus={focus} />)}</Tag>;
      })}
    </div>
  );
}
